// Story 6.17 — platform period close at /admin/finance/close.
// Wrapped by <AdminGuard> in App.tsx. Desktop-only.
//
// The SUBTRACTION, not a total: start at the four wallets' real balances and
// subtract, line by line, every euro that is not Greenhold's, ending at a
// spendable figure. If 6.7 last reported drift on a platform wallet, say so at
// the top instead of quietly presenting a total.

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@greenhold/components";

import {
  formatAge,
  formatBackingPerEuro,
  formatBpsPercent,
  formatCoverage,
  formatEur,
  useRecordBankBalance,
  useRecordRemittance,
  useRunSweep,
  usePlatformClose,
  useVatFilingReport,
  type BankLegView,
  type CloseWaterfall,
  type FilingReport,
} from "../features/admin/usePlatformClose";
// Story 11.8 — both threshold counters appear on THIS surface too, because
// "how close am I to having to register" is the same question as "how much is
// in which bucket". ⚑ Reuses Story 11.3's endpoint AND its panel component; it
// does not move or duplicate the panel logic.
import { useAdminMetrics } from "../features/admin/useAdminMetrics";
import { ThresholdCounterCard } from "./AdminMetricsPage";

function isDate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(s));
}

// The platform has not formally adopted a financial year yet. Denmark's DEFAULT
// financial year (regnskabsår) is the calendar year, so the close screen defaults
// to Jan 1 → Dec 31 of the current year. If the company later adopts a shifted FY
// (e.g. Jul 1 → Jun 30), change the month/day arithmetic HERE — this is the single
// source of the default range; the fields themselves stay freely editable.
function currentFinancialYear(): { from: string; to: string } {
  const year = new Date().getFullYear();
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

// ---------------------------------------------------------------------------
// Story 11.8 — the bank leg's status chip.
//
// ⚑ THE FAILURE THIS EXISTS TO PREVENT is an unverified number that looks
// verified. The chip is rendered NEXT TO the spendable figure, always, and it
// says which of the three states the leg is in and how old the reading is.
// ---------------------------------------------------------------------------
function BankLegBadge({ bank }: { bank: BankLegView }) {
  const tone =
    bank.status === "verified"
      ? "success"
      : bank.status === "stale"
        ? "warning"
        : "destructive";
  const text =
    bank.status === "verified"
      ? `Verified ${formatAge(bank.age_secs)}`
      : bank.status === "stale"
        ? `STALE — last read ${formatAge(bank.age_secs)}`
        : "UNVERIFIED — no bank balance recorded";
  return (
    <Badge variant={tone} data-testid="bank-leg-status">
      {text}
    </Badge>
  );
}

/// The spendable residual, the subtraction that produces it, and the bank leg's
/// status — inseparably.
function SpendablePanel({ data }: { data: CloseWaterfall }) {
  const s = data.spendable;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3">
          <span>Spendable — the only figure it is safe to spend from</span>
          <BankLegBadge bank={s.bank} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-gray-500 mb-3">
          One bank account holds user credit, the creator payable pot, the VAT reserve, the
          unresearched-country provisions <em>and</em> Greenhold&apos;s own money — separated only
          by ledger rows. The bank balance is therefore <strong>not</strong> a spending signal;
          this residual is. Current snapshot, not scoped to the period above.
        </p>

        {s.floor_breached ? (
          <div
            className="rounded border border-red-500 bg-red-50 p-3 text-red-900 mb-3"
            data-testid="floor-breach-banner"
          >
            <strong>RESERVE-SOLVENCY FLOOR BREACHED — this is not drift.</strong> Bank cash is{" "}
            {formatEur(s.floor_shortfall_cents)} below what is held for creators, SKAT and the
            unresearched-country provisions. Money held for someone else has been spent. See{" "}
            <code>docs/runbooks/reconciliation-drift.md</code> §G.
          </div>
        ) : null}

        {!s.verified ? (
          <div
            className="rounded border border-amber-400 bg-amber-50 p-3 text-amber-900 mb-3"
            data-testid="bank-unverified-banner"
          >
            <strong>This figure is not verified against real cash.</strong> It falls back to the
            ledger&apos;s own cash position because{" "}
            {s.bank.status === "stale"
              ? `the last bank balance was read ${formatAge(s.bank.age_secs)} (threshold ${Math.round(
                  s.bank.staleness_threshold_secs / 86_400,
                )}d)`
              : "no bank balance has been recorded"}
            . Record one below — with the date you actually read it.
            {s.bank.read_error ? ` Bank read failed: ${s.bank.read_error}` : ""}
          </div>
        ) : null}

        <table className="w-full text-sm" data-testid="spendable">
          <tbody>
            <tr className="border-b font-medium">
              <td className="py-1">
                Cash{" "}
                <span className="text-gray-500">
                  ({s.basis === "bank" ? "bank balance" : "ledger cash position — unverified"})
                </span>
              </td>
              <td className="py-1 text-right font-mono" data-testid="spendable-cash">
                {formatEur(s.cash_cents)}
              </td>
            </tr>
            <tr className="text-red-700">
              <td className="py-1">− creator-payable (earned pot + unopened-dossier liability)</td>
              <td className="py-1 text-right font-mono">
                −{formatEur(s.creator_payable_cents)}
              </td>
            </tr>
            <tr className="text-gray-500">
              <td className="py-1 pl-6">of which earned pot</td>
              <td className="py-1 text-right font-mono">
                {formatEur(s.creator_earnings_cents)}
              </td>
            </tr>
            <tr className="text-gray-500">
              <td className="py-1 pl-6">of which unopened-dossier liability</td>
              <td className="py-1 text-right font-mono">
                {formatEur(s.creator_liability_cents)}
              </td>
            </tr>
            <tr className="text-red-700">
              <td className="py-1">− VAT reserve (SKAT&apos;s, across every member state)</td>
              <td className="py-1 text-right font-mono">−{formatEur(s.vat_reserve_cents)}</td>
            </tr>
            <tr className="text-red-700">
              <td className="py-1">
                − unresearched-country provision (Story 11.9 — never on a VAT return, just as
                unspendable)
              </td>
              <td className="py-1 text-right font-mono">
                −{formatEur(s.unresearched_provision_cents)}
              </td>
            </tr>
            <tr className="border-t font-semibold">
              <td className="py-1">= SPENDABLE</td>
              <td className="py-1 text-right font-mono" data-testid="spendable-total">
                {formatEur(s.spendable_cents)}
              </td>
            </tr>
          </tbody>
        </table>

        {s.bank.as_of ? (
          <p className="text-xs text-gray-500 mt-2">
            Bank balance {formatEur(s.bank.balance_cents ?? 0)} as of{" "}
            <strong>{s.bank.as_of}</strong>, source <code>{s.bank.source}</code>
            {s.bank.reference ? ` · ${s.bank.reference}` : ""}.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/// Face credit outstanding vs the cash actually behind it (H1), plus the
/// solvency coverage number.
function BackingPanel({ data }: { data: CloseWaterfall }) {
  const b = data.backing;
  const sol = data.solvency;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Face credit vs the cash backing it</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-gray-500 mb-3">
          Face credit is <strong>not</strong> cash-backed 1:1 and is not meant to be. After the VAT
          toggle flips, one wallet balance holds euros bought untaxed <em>and</em> euros bought at
          a destination rate, in a proportion only the stamped top-up rows know — so the backing
          ratio below is <strong>derived from those rows</strong>, never assumed. A single-ratio
          assumption reads as drift on a correct ledger.
        </p>

        <table className="w-full text-sm" data-testid="backing">
          <tbody>
            <tr className="border-b font-medium">
              <td className="py-1">Face credit outstanding (users&apos; claim on us)</td>
              <td className="py-1 text-right font-mono">
                {formatEur(b.face_credit_outstanding_cents)}
              </td>
            </tr>
            <tr className="text-gray-500">
              <td className="py-1 pl-6">of which escrowed against open bids/offers</td>
              <td className="py-1 text-right font-mono">
                {formatEur(b.face_credit_escrow_cents)}
              </td>
            </tr>
            <tr>
              <td className="py-1">Gross topped up, all time (stamped)</td>
              <td className="py-1 text-right font-mono">{formatEur(b.stamped_gross_cents)}</td>
            </tr>
            <tr className="text-red-700">
              <td className="py-1">− output VAT stamped on those top-ups</td>
              <td className="py-1 text-right font-mono">
                −{formatEur(b.stamped_output_vat_cents)}
              </td>
            </tr>
            <tr className="text-red-700">
              <td className="py-1">
                − acquiring at {formatBpsPercent(b.acquiring_fee_bps)}{" "}
                <span className="text-gray-500">
                  (an assumption — no PSP fee leg exists in the ledger)
                </span>
              </td>
              <td className="py-1 text-right font-mono">−{formatEur(b.acquiring_cost_cents)}</td>
            </tr>
            <tr className="border-t font-semibold">
              <td className="py-1">= net cash pool behind the face credit</td>
              <td className="py-1 text-right font-mono" data-testid="net-cash-pool">
                {formatEur(b.net_cash_pool_cents)}
              </td>
            </tr>
            <tr className="border-t">
              <td className="py-1">Cash backing per face euro (derived)</td>
              <td className="py-1 text-right font-mono" data-testid="backing-per-euro">
                {formatBackingPerEuro(b.backing_per_face_euro_bps)}
              </td>
            </tr>
            <tr className="text-gray-500">
              <td className="py-1 pl-6">
                from a {b.mixed_pool ? "MIXED" : "single-backing"} pool: taxed{" "}
                {formatEur(b.taxed_gross_cents)} · untaxed {formatEur(b.untaxed_gross_cents)}
              </td>
              <td className="py-1 text-right font-mono" />
            </tr>
            <tr className="text-gray-500">
              <td className="py-1">Ledger cash position (Σ into the perimeter − Σ out)</td>
              <td className="py-1 text-right font-mono">{formatEur(b.ledger_cash_cents)}</td>
            </tr>
          </tbody>
        </table>

        <h3 className="text-sm font-semibold mt-5 mb-1">Solvency coverage (invariant I2)</h3>
        <p className="text-xs text-gray-500 mb-2">
          The worst case the outstanding face credit can create is{" "}
          <strong>g = {formatBpsPercent(sol.g_bps)}</strong> of it (the creator side of a primary
          sale, read from the splitter itself). Coverage is the net cash pool against that.
        </p>
        <table className="w-full text-sm" data-testid="solvency">
          <tbody>
            <tr>
              <td className="py-1">Worst-case obligation (g × face outstanding)</td>
              <td className="py-1 text-right font-mono">
                {formatEur(sol.worst_case_obligation_cents)}
              </td>
            </tr>
            <tr className={sol.breached ? "font-bold text-red-700" : "font-semibold"}>
              <td className="py-1">
                = COVERAGE {sol.breached ? "— BELOW 1.00x, investigate (runbook §E)" : ""}
              </td>
              <td className="py-1 text-right font-mono" data-testid="coverage">
                {formatCoverage(sol.coverage_bps)}
              </td>
            </tr>
          </tbody>
        </table>
        <p className="text-xs text-gray-500 mt-2">
          Reference, per €100.00 of face credit, computed from the same inputs:{" "}
          <strong>{formatCoverage(sol.reference_toggle_off.coverage_bps)}</strong> while not
          VAT-registered (€100.00 − VAT {formatEur(sol.reference_toggle_off.vat_cents)} − acquiring{" "}
          {formatEur(sol.reference_toggle_off.acquiring_cents)} ={" "}
          {formatEur(sol.reference_toggle_off.net_cash_pool_cents)} against{" "}
          {formatEur(sol.reference_toggle_off.worst_case_obligation_cents)})
          {sol.reference_dk_registered ? (
            <>
              ; <strong>{formatCoverage(sol.reference_dk_registered.coverage_bps)}</strong> at
              Denmark&apos;s {formatBpsPercent(sol.reference_dk_registered.vat_rate_bps)} (VAT{" "}
              {formatEur(sol.reference_dk_registered.vat_cents)} →{" "}
              {formatEur(sol.reference_dk_registered.net_cash_pool_cents)} against{" "}
              {formatEur(sol.reference_dk_registered.worst_case_obligation_cents)}), the rate read
              from the country register.
            </>
          ) : (
            <>. Denmark&apos;s rate is not in the country register, so its reference is omitted
            rather than guessed.</>
          )}
        </p>
      </CardContent>
    </Card>
  );
}

/// Story 11.8 — the momsangivelse-shaped filing report.
function FilingReportPanel({ report }: { report: FilingReport }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>VAT filing report (momsangivelse shape)</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className="rounded border border-amber-400 bg-amber-50 p-3 text-amber-900 text-xs mb-3"
          data-testid="provisional-basis"
        >
          <strong>{report.provisional_basis}</strong>
          <div className="mt-1">Field mapping: {report.field_mapping_status}</div>
        </div>

        {report.any_stamp_ledger_mismatch ? (
          <div className="rounded border border-red-400 bg-red-50 p-3 text-red-800 text-xs mb-3">
            <strong>A stamped VAT amount disagrees with its ledger legs.</strong> Do not file
            against this period until it is explained — the mismatch is flagged per row below.
          </div>
        ) : null}

        <table className="w-full text-sm mb-4" data-testid="filing-boxes">
          <tbody>
            <tr className="border-b font-medium">
              <td className="py-1">Salgsmoms (Udgående moms) — output VAT at top-up, net of refunds</td>
              <td className="py-1 text-right font-mono">{formatEur(report.salgsmoms_cents)}</td>
            </tr>
            <tr>
              <td className="py-1">Købsmoms (indgående moms) — deductible input VAT</td>
              <td className="py-1 text-right font-mono">{formatEur(report.koebsmoms_cents)}</td>
            </tr>
            <tr>
              <td className="py-1">
                Moms af ydelseskøb i udlandet med omvendt betalingspligt — reverse-charge{" "}
                <strong>output</strong>
              </td>
              <td className="py-1 text-right font-mono">
                {formatEur(report.reverse_charge_output_cents)}
              </td>
            </tr>
            <tr className="text-gray-500">
              <td className="py-1 pl-6">
                reverse-charge <strong>input</strong> (shown separately — ⛔ never netted at write
                time; it feeds a different box and only the output leg survives a denial of W9)
              </td>
              <td className="py-1 text-right font-mono">
                {formatEur(report.reverse_charge_input_cents)}
              </td>
            </tr>
            <tr className="text-gray-500">
              <td className="py-1">Rubrik A - ydelser — net VALUE of EU service purchases</td>
              <td className="py-1 text-right font-mono">
                {formatEur(report.rubrik_a_ydelser_cents)}
              </td>
            </tr>
            <tr className="border-t font-semibold">
              <td className="py-1">= NET VAT PAYABLE</td>
              <td className="py-1 text-right font-mono" data-testid="net-vat-payable">
                {formatEur(report.net_vat_payable_cents)}
              </td>
            </tr>
            <tr className="text-gray-500">
              <td className="py-1">if the input deduction is denied (W9)</td>
              <td className="py-1 text-right font-mono">
                {formatEur(report.net_vat_payable_without_deduction_cents)}
              </td>
            </tr>
          </tbody>
        </table>

        <p className="text-xs text-amber-800 mb-4" data-testid="deductible-input-note">
          {report.deductible_input_note}
        </p>

        <h3 className="text-sm font-semibold mb-1">Output VAT by the state it is OWED to</h3>
        <p className="text-xs text-gray-500 mb-2">
          ⚑ Grouped by the state the money is <strong>owed</strong> to, not by where the buyer
          was. Below the EUR 10,000 cross-border threshold a buyer in another member state is a{" "}
          <em>supplier-placed</em> supply: Denmark&apos;s rate, Denmark&apos;s return. The
          buyer&apos;s state is shown beside it because both questions matter.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="filing-by-owed-state">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-1">Owed to</th>
                <th className="py-1">Buyer</th>
                <th className="py-1">Rail</th>
                <th className="py-1">Phase</th>
                <th className="py-1 text-right">Top-ups</th>
                <th className="py-1 text-right">Gross</th>
                <th className="py-1 text-right">VAT</th>
                <th className="py-1">Field</th>
              </tr>
            </thead>
            <tbody>
              {report.output_vat.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-2 text-gray-400">
                    No stamped top-ups in this period.
                  </td>
                </tr>
              ) : (
                report.output_vat.map((r) => (
                  <tr
                    key={`${r.owed_state}-${r.buyer_state}-${r.regime}-${r.registration_state}`}
                    className={r.stamp_ledger_mismatch ? "text-red-700" : ""}
                  >
                    <td className="py-1 font-medium">{r.owed_state}</td>
                    <td className="py-1">{r.buyer_state}</td>
                    <td className="py-1" title={r.rail_label}>
                      {r.rail}
                    </td>
                    <td className="py-1">{r.registration_state}</td>
                    <td className="py-1 text-right font-mono">{r.topup_count}</td>
                    <td className="py-1 text-right font-mono">{formatEur(r.gross_cents)}</td>
                    <td className="py-1 text-right font-mono">
                      {formatEur(r.ledger_vat_cents)}
                      {r.stamp_ledger_mismatch
                        ? ` (stamp says ${formatEur(r.stamped_vat_cents)})`
                        : ""}
                    </td>
                    <td className="py-1 text-xs text-gray-500">{r.momsangivelse_field}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {report.unlocatable_topup_count > 0 ? (
          <p className="text-xs text-gray-600 mt-2">
            {report.unlocatable_topup_count} top-up(s) totalling{" "}
            {formatEur(report.unlocatable_gross_cents)} could not be located to any country. Shown,
            never defaulted to Denmark — a recorded gap beats manufactured evidence.
          </p>
        ) : null}

        {report.refund_reversals.length > 0 ? (
          <>
            <h3 className="text-sm font-semibold mt-5 mb-1">Refund reversals (negative output VAT)</h3>
            <table className="w-full text-sm">
              <tbody>
                {report.refund_reversals.map((r) => (
                  <tr key={r.owed_state}>
                    <td className="py-1">
                      {r.owed_state} — {r.reversal_count} reversal(s)
                    </td>
                    <td className="py-1 text-right font-mono">
                      −{formatEur(r.reversed_vat_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}

        <h3 className="text-sm font-semibold mt-5 mb-1">Creator-side VAT lines</h3>
        {report.creator_vat.length === 0 ? (
          <p className="text-xs text-gray-500">
            None. No creator self-billed input-VAT row exists in the ledger — this is what
            &ldquo;renders what exists&rdquo; looks like while W9 is open, not a zero anyone
            asserted.
          </p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {report.creator_vat.map((r) => (
                <tr key={`${r.line}-${r.establishment}`}>
                  <td className="py-1">
                    {r.label}{" "}
                    <span className="text-gray-500">
                      ({r.establishment}, {r.settlement_count} settlement(s))
                    </span>
                    {r.note ? (
                      <div className="text-xs text-amber-800">{r.note}</div>
                    ) : null}
                  </td>
                  <td className="py-1 text-right font-mono">{formatEur(r.amount_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h3 className="text-sm font-semibold mt-5 mb-1">
          Unresearched countries — accepted at 0%
        </h3>
        <p className="text-xs text-gray-500 mb-2">
          An accepted risk that is visible on the filing surface is an accepted risk; one that is
          invisible is an omission. The reserve beside each row is Story 11.9&apos;s provision —
          held back, and ⛔ never on a VAT return.
        </p>
        {report.unresearched.length === 0 ? (
          <p className="text-xs text-gray-500">None in this period.</p>
        ) : (
          <table className="w-full text-sm" data-testid="filing-unresearched">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-1">Country</th>
                <th className="py-1 text-right">Top-ups</th>
                <th className="py-1 text-right">Gross</th>
                <th className="py-1 text-right">Charged</th>
                <th className="py-1 text-right">Reserve held</th>
              </tr>
            </thead>
            <tbody>
              {report.unresearched.map((r) => (
                <tr key={r.country}>
                  <td className="py-1">{r.country}</td>
                  <td className="py-1 text-right font-mono">{r.topup_count}</td>
                  <td className="py-1 text-right font-mono">{formatEur(r.gross_cents)}</td>
                  <td className="py-1 text-right font-mono">
                    {formatEur(r.charged_vat_cents)}
                  </td>
                  <td className="py-1 text-right font-mono">
                    {formatEur(r.provision_held_cents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

export function AdminPlatformClosePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const fy = currentFinancialYear();
  const [from, setFrom] = useState(searchParams.get("from") ?? fy.from);
  const [to, setTo] = useState(searchParams.get("to") ?? fy.to);
  const ready = isDate(from) && isDate(to) && from <= to;

  const close = usePlatformClose(from, to, ready);
  const sweep = useRunSweep();
  const remit = useRecordRemittance();
  // Story 11.8 — the filing report for the same period, the bank-balance write,
  // and Story 11.3's threshold counters read from their own endpoint.
  const filing = useVatFilingReport(from, to, ready);
  const recordBank = useRecordBankBalance();
  const metrics = useAdminMetrics();
  // `PanelEnvelope` discriminates on the presence of `data`, exactly as the
  // metrics page's own `isWired` does.
  const vatEnvelope = metrics.data?.data.vat_registration;
  const vatPanel =
    vatEnvelope && "data" in vatEnvelope ? vatEnvelope.data : null;

  // Bank-balance form state. ⚑ `bankAsOf` has no default: the operator must
  // state the date they actually read the balance.
  const [bankBalance, setBankBalance] = useState("");
  const [bankAsOf, setBankAsOf] = useState("");
  const [bankReference, setBankReference] = useState("");
  const [bankSource, setBankSource] = useState<"manual" | "statement_import">("manual");

  // Remittance form state.
  const [country, setCountry] = useState("DK");
  const [remitted, setRemitted] = useState("");
  const [offset, setOffset] = useState("0");
  const [note, setNote] = useState("");
  const [reference, setReference] = useState("");

  function loadRange() {
    if (!ready) {
      toast.error("Enter both dates as YYYY-MM-DD, from ≤ to");
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("from", from);
    next.set("to", to);
    setSearchParams(next, { replace: true });
  }

  const data = close.data;

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-platform-close-page">
      <div>
        <h1 className="text-2xl font-semibold">Platform period close</h1>
        <p className="text-sm text-gray-500 mt-1">
          What is mine, what is owed, and why. Danish VAT is filed per period (quarterly or
          half-yearly), so pick an arbitrary range.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Period</CardTitle>
        </CardHeader>
        <CardContent className="flex items-end gap-4">
          <label className="text-sm">
            From
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 block px-3 py-2 border rounded font-mono text-sm"
              aria-label="period from"
            />
          </label>
          <label className="text-sm">
            To
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 block px-3 py-2 border rounded font-mono text-sm"
              aria-label="period to"
            />
          </label>
          <Button variant="primary" onClick={loadRange} data-testid="load-period">
            Load
          </Button>
        </CardContent>
      </Card>

      {close.isError ? (
        <Card>
          <CardContent className="text-red-600 py-4">{close.error.message}</CardContent>
        </Card>
      ) : null}

      {data ? (
        <>
          {data.reconciliation_drift.length > 0 ? (
            <Card>
              <CardContent className="py-4">
                <div
                  className="rounded border border-red-400 bg-red-50 p-3 text-red-800"
                  data-testid="drift-banner"
                >
                  <strong>Reconciliation drift on a platform wallet.</strong> These totals may be
                  wrong — do not close against them until resolved:
                  <ul className="list-disc ml-6 mt-1">
                    {data.reconciliation_drift.map((d) => (
                      <li key={d.wallet_kind}>
                        {d.wallet_kind}: {d.status}, {formatEur(d.drift_cents)}
                        {d.detail ? ` — ${d.detail}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>The waterfall</CardTitle>
            </CardHeader>
            <CardContent>
              {data.wallets_from_ledger ? (
                <p className="text-xs text-amber-700 mb-2">
                  Platform wallets are not yet provisioned at the provider; figures fall back to
                  ledger-account balances (segregation is ledger-only for now).
                </p>
              ) : null}
              <table className="w-full text-sm" data-testid="waterfall">
                <tbody>
                  <tr className="border-b font-medium">
                    <td className="py-1">Total across Greenhold&apos;s wallets</td>
                    <td className="py-1 text-right font-mono">
                      {formatEur(data.wallet_total_cents)}
                    </td>
                  </tr>
                  <tr className="text-red-700">
                    <td className="py-1">− platform_vat (not yet remitted)</td>
                    <td className="py-1 text-right font-mono">
                      −{formatEur(data.vat_wallet_cents)}
                    </td>
                  </tr>
                  <tr className="text-red-700">
                    <td className="py-1">− platform_creator_liability (unopened dossiers)</td>
                    <td className="py-1 text-right font-mono">
                      −{formatEur(data.creator_liability_wallet_cents)}
                    </td>
                  </tr>
                  <tr className="text-red-700">
                    <td className="py-1">
                      − platform_reserve (chargeback)
                      {!data.reserve_modelled ? " — not yet modelled" : ""}
                    </td>
                    <td className="py-1 text-right font-mono">
                      −{formatEur(data.reserve_wallet_cents)}
                    </td>
                  </tr>
                  <tr className="border-t font-semibold">
                    <td className="py-1">= AVAILABLE TO SPEND (platform_revenue)</td>
                    <td className="py-1 text-right font-mono" data-testid="available">
                      {formatEur(data.available_cents)}
                    </td>
                  </tr>
                  <tr className="text-gray-500">
                    <td className="py-1">of which unswept (VAT/liability not yet moved)</td>
                    <td className="py-1 text-right font-mono">{formatEur(data.unswept_cents)}</td>
                  </tr>
                  {data.unexplained_cents !== 0 ? (
                    <tr className="text-red-700 font-bold" data-testid="unexplained">
                      <td className="py-1">UNEXPLAINED (does not reconcile — investigate)</td>
                      <td className="py-1 text-right font-mono">
                        {formatEur(data.unexplained_cents)}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
              <div className="mt-3">
                <Button
                  variant="secondary"
                  disabled={sweep.isPending}
                  onClick={() =>
                    sweep
                      .mutateAsync()
                      .then(() => toast.success("Sweep complete"))
                      .catch((e) => toast.error(e instanceof Error ? e.message : "Sweep failed"))
                  }
                  data-testid="run-sweep"
                >
                  Run sweep (match wallets to ledger)
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Story 11.8 — the three surfaces the single-bank-account decision
              made necessary. They sit directly under the waterfall because the
              spendable figure is what the waterfall's "AVAILABLE TO SPEND" line
              was reaching for and could not reach. */}
          <SpendablePanel data={data} />

          <Card>
            <CardHeader>
              <CardTitle>Record the bank balance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-gray-500">
                Enter what the bank actually says, and <strong>the date you read it</strong>. There
                is no default date and no &ldquo;now&rdquo; button on purpose: a balance with a
                manufactured date is exactly the unverified-looking-verified number this screen
                exists to prevent. Entries are append-only — correct a wrong figure by recording a
                new one. <strong>This records a reading; it moves no money.</strong>
              </p>
              <div className="flex flex-wrap gap-3">
                <label className="text-sm">
                  Source
                  <select
                    value={bankSource}
                    onChange={(e) =>
                      setBankSource(e.target.value as "manual" | "statement_import")
                    }
                    className="mt-1 block px-2 py-1 border rounded text-sm"
                    aria-label="bank balance source"
                  >
                    <option value="manual">manual</option>
                    <option value="statement_import">statement_import</option>
                  </select>
                </label>
                <label className="text-sm">
                  Balance (cents)
                  <input
                    type="number"
                    value={bankBalance}
                    onChange={(e) => setBankBalance(e.target.value)}
                    className="mt-1 block px-2 py-1 border rounded font-mono"
                    aria-label="bank balance cents"
                  />
                </label>
                <label className="text-sm">
                  As of
                  <input
                    type="datetime-local"
                    value={bankAsOf}
                    onChange={(e) => setBankAsOf(e.target.value)}
                    className="mt-1 block px-2 py-1 border rounded font-mono text-sm"
                    aria-label="bank balance as of"
                  />
                </label>
                <label className="text-sm flex-1 min-w-[12rem]">
                  Reference
                  <input
                    type="text"
                    value={bankReference}
                    onChange={(e) => setBankReference(e.target.value)}
                    placeholder="statement line / bank reference"
                    className="mt-1 block w-full px-2 py-1 border rounded text-sm"
                    aria-label="bank balance reference"
                  />
                </label>
              </div>
              <Button
                variant="secondary"
                disabled={recordBank.isPending}
                data-testid="record-bank-balance"
                onClick={() => {
                  const cents = Number.parseInt(bankBalance, 10);
                  if (Number.isNaN(cents)) {
                    toast.error("Balance must be an integer number of cents");
                    return;
                  }
                  if (!bankAsOf) {
                    toast.error("Enter the date and time the balance was read");
                    return;
                  }
                  const asOf = new Date(bankAsOf);
                  if (Number.isNaN(asOf.getTime())) {
                    toast.error("As-of is not a valid date/time");
                    return;
                  }
                  recordBank
                    .mutateAsync({
                      source: bankSource,
                      balance_cents: cents,
                      currency: "EUR",
                      as_of: asOf.toISOString(),
                      reference: bankReference || undefined,
                    })
                    .then(() => {
                      toast.success("Bank balance recorded");
                      setBankBalance("");
                      setBankAsOf("");
                      setBankReference("");
                    })
                    .catch((e) =>
                      toast.error(e instanceof Error ? e.message : "Recording failed"),
                    );
                }}
              >
                Record balance
              </Button>
            </CardContent>
          </Card>

          <BackingPanel data={data} />

          {/* Story 11.3's two counters, on this surface too — same endpoint,
              same component, no second copy of the panel logic. */}
          {vatPanel ? (
            <Card>
              <CardHeader>
                <CardTitle>How close are we to having to register?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-gray-500 mb-3">
                  Two independent counters, either of which can trip first: the Danish domestic
                  registration threshold and the EU-wide cross-border place-of-supply threshold.
                  Registration state today: <strong>{vatPanel.current.state}</strong>.
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  <ThresholdCounterCard counter={vatPanel.counters.danish_domestic} />
                  <ThresholdCounterCard counter={vatPanel.counters.eu_cross_border} />
                </div>
              </CardContent>
            </Card>
          ) : null}

          {filing.isError ? (
            <Card>
              <CardContent className="text-red-600 py-4">{filing.error.message}</CardContent>
            </Card>
          ) : null}
          {filing.data ? <FilingReportPanel report={filing.data} /> : null}

          <Card>
            <CardHeader>
              <CardTitle>Output VAT by member state</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-gray-500 mb-2">
                Accrued this period, grouped by <strong>where the buyer was</strong> (the movement
                header). ⚠ That is <em>not</em> the same question as which return the money goes
                on — for that, read the filing report above, which groups by the state the money
                is <strong>owed</strong> to. The <code>unknown</code> bucket is shown, not hidden.
              </p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-1">Country</th>
                    <th className="py-1">Source</th>
                    <th className="py-1 text-right">VAT</th>
                  </tr>
                </thead>
                <tbody>
                  {data.vat_by_country.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-2 text-gray-400">
                        No output VAT accrued in this period.
                      </td>
                    </tr>
                  ) : (
                    data.vat_by_country.map((v) => (
                      <tr key={`${v.country}-${v.country_source}`}>
                        <td className="py-1">{v.country}</td>
                        <td className="py-1">{v.country_source}</td>
                        <td className="py-1 text-right font-mono">{formatEur(v.vat_cents)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>User holdings (current snapshot)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-gray-500 mb-2">
                Money the platform <strong>holds but does not own</strong> — a user liability, not
                platform funds. This is a <strong>point-in-time snapshot of right now</strong>, not a
                figure for the selected period.
              </p>
              <table className="w-full text-sm" data-testid="user-holdings">
                <tbody>
                  <tr className="border-b font-medium">
                    <td className="py-1">Total held for users (balance + escrow)</td>
                    <td className="py-1 text-right font-mono" data-testid="user-holdings-total">
                      {formatEur(data.user_holdings_total_cents)}
                    </td>
                  </tr>
                  <tr className="text-gray-500">
                    <td className="py-1">of which withheld for bids/offers (escrow)</td>
                    <td className="py-1 text-right font-mono" data-testid="user-holdings-escrow">
                      {formatEur(data.user_holdings_escrow_cents)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>

          {data.previous_period ? (
            <Card>
              <CardHeader>
                <CardTitle>Previous period (for shape)</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <p>
                  {data.previous_period.period_from} → {data.previous_period.period_to}: output VAT{" "}
                  {formatEur(data.previous_period.output_vat_accrued_cents)}, platform revenue{" "}
                  {formatEur(data.previous_period.platform_revenue_accrued_cents)}
                </p>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>File a VAT remittance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-gray-500">
                The platform ledger sees <strong>output VAT only</strong>. The input-VAT (købsmoms)
                offset is a figure your accountant supplies and this system records — it is not
                calculated here. Remitting closes the period; a correction is a new movement, never
                an edit.
              </p>
              <div className="flex gap-3">
                <label className="text-sm">
                  Country
                  <input
                    type="text"
                    value={country}
                    maxLength={2}
                    onChange={(e) => setCountry(e.target.value.toUpperCase())}
                    className="mt-1 block w-20 px-2 py-1 border rounded font-mono"
                    aria-label="remittance country"
                  />
                </label>
                <label className="text-sm">
                  Remitted (cents)
                  <input
                    type="number"
                    value={remitted}
                    onChange={(e) => setRemitted(e.target.value)}
                    className="mt-1 block px-2 py-1 border rounded font-mono"
                    aria-label="remitted cents"
                  />
                </label>
                <label className="text-sm">
                  Input-VAT offset (cents)
                  <input
                    type="number"
                    value={offset}
                    onChange={(e) => setOffset(e.target.value)}
                    className="mt-1 block px-2 py-1 border rounded font-mono"
                    aria-label="input vat offset cents"
                  />
                </label>
              </div>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="SKAT payment reference"
                className="w-full px-3 py-2 border rounded text-sm"
                aria-label="payment reference"
              />
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Note (names the accountant + date)"
                className="w-full px-3 py-2 border rounded text-sm"
                aria-label="remittance note"
              />
              <Button
                variant="destructive"
                disabled={remit.isPending}
                data-testid="file-remittance"
                onClick={() => {
                  const r = Number.parseInt(remitted, 10);
                  const o = Number.parseInt(offset || "0", 10);
                  if (Number.isNaN(r) || r < 0) {
                    toast.error("Remitted must be a non-negative integer (cents)");
                    return;
                  }
                  remit
                    .mutateAsync({
                      period_from: from,
                      period_to: to,
                      country,
                      remitted_cents: r,
                      input_vat_offset_cents: Number.isNaN(o) ? 0 : o,
                      payment_reference: reference || undefined,
                      note: note || undefined,
                    })
                    .then(() => toast.success("Remittance filed"))
                    .catch((e) =>
                      toast.error(e instanceof Error ? e.message : "Remittance failed"),
                    );
                }}
              >
                File remittance (closes {country} for this period)
              </Button>
            </CardContent>
          </Card>

          {data.filings.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Filed for this period</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="py-1">Country</th>
                      <th className="py-1 text-right">Accrued</th>
                      <th className="py-1 text-right">Offset</th>
                      <th className="py-1 text-right">Remitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.filings.map((f) => (
                      <tr key={f.id}>
                        <td className="py-1">{f.country}</td>
                        <td className="py-1 text-right font-mono">
                          {formatEur(f.output_vat_accrued_cents)}
                        </td>
                        <td className="py-1 text-right font-mono">
                          {formatEur(f.input_vat_offset_cents)}
                        </td>
                        <td className="py-1 text-right font-mono">
                          {formatEur(f.remitted_cents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export default AdminPlatformClosePage;
